import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe2, ArrowRight, Check, Search, X } from "lucide-react";
import { Reveal } from "../Reveal";
import { COUNTRIES_COUNT, PHONE, PHONE_HREF } from "../../lib/site";
import { COUNTRY_LIST } from "../../lib/countries";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export const VisaCountries = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? COUNTRY_LIST.filter((c) => c.name.toLowerCase().includes(q)) : COUNTRY_LIST),
    [q]
  );

  const goToFirst = (e) => {
    e.preventDefault();
    if (filtered.length > 0) {
      scrollTop();
      navigate(`/visa/${filtered[0].slug}`);
    }
  };

  return (
    <section id="countries" className="relative bg-[#0b192c] text-white py-24 md:py-32 overflow-hidden" data-testid="visa-countries">
      <div className="noise-overlay" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-wide">
                <Globe2 className="w-4 h-4 text-[#ef233c]" /> Global visa coverage
              </div>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
                Visa services for<br />
                <span className="text-[#ef233c]">{COUNTRIES_COUNT} countries.</span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">
                From business trips to family vacations, our specialists process tourist and business visas for destinations across Africa, Asia, the Middle East, Europe, and Latin America — hand-carried to the embassies right here in Washington DC.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/visa-services-washington-dc" onClick={scrollTop} className="inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-6 py-3.5 font-semibold hover:bg-[#ef233c] transition-colors" data-testid="countries-cta">
                  Explore visa services <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white px-6 py-3.5 font-semibold hover:bg-white hover:text-[#0b192c] transition-colors">
                  Call {PHONE}
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            {/* Country search */}
            <form onSubmit={goToFirst} className="mb-6" role="search">
              <label htmlFor="country-search" className="sr-only">Search countries</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#ef233c] pointer-events-none" />
                <input
                  id="country-search"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search a country (e.g. India, Brazil, Kenya)…"
                  autoComplete="off"
                  className="w-full rounded-full bg-white/5 border border-white/15 pl-12 pr-40 py-3.5 text-[15px] text-white placeholder:text-white/40 focus:outline-none focus:border-[#ef233c] focus:bg-white/10 transition-colors"
                  data-testid="country-search-input"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-32 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
                    aria-label="Clear search"
                    data-testid="country-search-clear"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
                <button
                  type="submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#ef233c] transition-colors"
                  aria-label="Search countries"
                  data-testid="country-search-submit"
                >
                  <Search className="w-4 h-4" /> Search
                </button>
              </div>
              {q && (
                <p className="mt-2.5 pl-1 text-sm text-white/50" data-testid="country-search-count">
                  {filtered.length === 0
                    ? `No match for "${query}". Call ${PHONE} — we likely still handle it.`
                    : `${filtered.length} ${filtered.length === 1 ? "country" : "countries"} found — press Enter to open the first.`}
                </p>
              )}
            </form>

            <div className="flex flex-wrap gap-2.5" data-testid="country-chips">
              {filtered.map((c, i) => (
                <motion.span
                  key={c.slug}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: Math.min(i * 0.012, 0.5) }}
                >
                  <Link
                    to={`/visa/${c.slug}`}
                    onClick={scrollTop}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-sm text-white/85 hover:border-[#ef233c] hover:bg-white/10 transition-colors"
                    data-testid={`country-${i}`}
                  >
                    <Check className="w-3 h-3 text-[#ef233c]" /> {c.name}
                  </Link>
                </motion.span>
              ))}
              {!q && (
                <Link to="/visa-services-washington-dc" onClick={scrollTop} className="inline-flex items-center rounded-full bg-[#d90429] px-3.5 py-1.5 text-sm font-semibold text-white hover:bg-[#ef233c] transition-colors">
                  + many more
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
